import React, { FC, useEffect, useState, useCallback, useMemo } from 'react'
import { Button } from '@corratech/pylot-ui'
import { Logo } from '@components/icons'
import { useAuthUI } from '@corratech/pylot-auth-manager'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import { useTranslation } from 'next-i18next/pages'
import { GraphQLError } from '@pylot-data/graphqlFetch'
import { useAuthLogin } from '@lib/casaideas-components/pylot-auth-manager/src/use-auth-login'
import { Cart } from '@pylot-data/pylotschema'
import { CartData } from '@lib/casaideas-components/pylot-cart-manager'
import FormInput from '@components/common/FormInput/FormInput'
import { LoginFormElements, LoginFormErrors } from './types'
import { validateForm } from './utils/loginFormValidator'
import cn from 'classnames'
import s from './Auth.module.scss'
import { GoogleAuthView } from '@components/GoogleLogin'
import { AlertMessage } from '@components/common/AlertMessage'
export const passwordMinLength = 8
export const passwordRegExp = new RegExp(/^(?=.*\d)(?=.*[a-zA-Z]).{8,}$/)

type LoginViewProps = {
    updateCart: (data?: {
        data: {
            cart: Cart
            isOptimistic?: boolean
        }
        errors?: GraphQLError[]
    }) => Promise<any>
    cartData?: CartData
}

const LoginView: FC<LoginViewProps> = ({
    updateCart,
    cartData
}: LoginViewProps) => {
    const { t } = useTranslation('common')

    // Form State
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState('')
    const [dirty, setDirty] = useState(false)
    const [disabled, setDisabled] = useState(false)
    const [formErrors, setFormErrors] = useState<LoginFormErrors>(
        {} as LoginFormErrors
    )

    const { login } = useAuthLogin({ cartData, updateCart })
    const { setModalView, closeAuthModal } = useAuthUI()
    const { openToast } = useUI()

    const handleLogin = async (e: React.FormEvent<LoginFormElements>) => {
        e.preventDefault()

        const { errors, hasErrors } = validateForm({ email, password }, t)
        if (!hasErrors) {
            try {
                setLoading(true)
                setMessage('')
                await login({
                    email,
                    password
                })
                closeAuthModal()
                openToast('Ingreso exitoso!', ToastType.Success, true)
            } catch (errors) {
                setPassword('')
                setMessage(
                    //@ts-ignore
                    errors?.[0]?.message || t('404|Something went wrong.')
                )
                setLoading(false)
                setDisabled(false)
            }
        } else {
            setDirty(hasErrors)
            setDisabled(hasErrors)
            setFormErrors(errors)
        }
    }

    const handleValidation = useCallback(() => {
        if (dirty) {
            const { errors, hasErrors } = validateForm({ email, password }, t)
            setDisabled(hasErrors)
            setFormErrors(errors)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dirty, email, password])

    useEffect(() => {
        handleValidation()
    }, [handleValidation])

    const googleComponent = useMemo(
        () => (
            <div className={s['google-container']}>
                <GoogleAuthView
                    closeAuthModal={closeAuthModal}
                    updateCart={updateCart}
                    buttonShape="pill"
                />
            </div>
        ),
        [closeAuthModal, updateCart]
    )

    return (
        <form
            onSubmit={handleLogin}
            className={cn(s['login-view-form'], 'login-form')}
            noValidate
        >
            <div className={cn(s['form-logo'], 'form-logo')}>
                <Logo className={s.logo} />
                <p id="login-panel-title" className={s.welcomeTitle}>
                    {t('auth|Sign in title', {
                        defaultValue: 'Ingresa a tu cuenta'
                    })}
                </p>
                <p className={s.welcomeSubtitle}>
                    {t('auth|Welcome back', {
                        defaultValue: '¡Nos alegra tenerte de vuelta!'
                    })}
                </p>
            </div>

            {message && (
                <div className={s['message-wrap']}>
                    <AlertMessage type="danger">
                        <span>{message}</span>
                        <span
                            className={s['message-txt']}
                            onClick={() =>
                                setModalView('FORGOT_VIEW', {
                                    email
                                })
                            }
                            aria-hidden="true"
                        >
                            {t('auth|Did you forget your password?')}
                        </span>
                    </AlertMessage>
                </div>
            )}

            <div className="form-group">
                <FormInput
                    variant="line"
                    type="email"
                    id="email"
                    name={t('auth|Email')}
                    value={email}
                    onChange={setEmail}
                    error={formErrors?.email}
                />
            </div>

            <div className="form-group" key="password">
                <FormInput
                    variant="line"
                    type="password"
                    id="password"
                    name={t('auth|Password')}
                    value={password}
                    onChange={setPassword}
                    error={formErrors?.password}
                />
            </div>

            <div className={cn(s['form-actions'], 'form-actions')}>
                <Button
                    variant="slim"
                    type="submit"
                    loading={loading}
                    disabled={disabled}
                    className={s.submit}
                    aria-label={t('auth|Sign in button', {
                        defaultValue: 'Iniciar sesión'
                    })}
                >
                    {t('auth|Sign in button', {
                        defaultValue: 'Iniciar sesión'
                    })}
                </Button>
                {googleComponent}
                <div className={s['form-below-options']}>
                    <span className="text-accents-7">
                        {t("auth|Don't have an account?")}{' '}
                    </span>
                    <button
                        type="button"
                        className={cn(s['signup-link'], 'signup-link')}
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            setModalView('SIGNUP_VIEW')
                        }}
                    >
                        {t('auth|Sign Up here').toLocaleLowerCase()}
                    </button>
                </div>
                <div
                    className={cn(
                        s['form-below-options'],
                        s['forgot-password']
                    )}
                >
                    <span className="text-accents-7">
                        {t('auth|Did you forget your password?')},{' '}
                    </span>
                    <button
                        type="button"
                        className={cn(
                            s['signup-link'],
                            'signup-link lowercase'
                        )}
                        onClick={(event) => {
                            event.preventDefault()
                            event.stopPropagation()
                            setModalView('FORGOT_VIEW', { email })
                        }}
                    >
                        {t('auth|Recover here')}
                    </button>
                </div>
            </div>
        </form>
    )
}

export default LoginView
